// src/Announcements.js - NUOVO COMPONENTE BACHECA

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, query, orderBy, limit, onSnapshot, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useUser } from './UserContext';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FaBullhorn, FaTrash, FaEdit, FaPlus } from 'react-icons/fa';

// Stili semplici inline per layout
const styles = {
    container: { maxWidth: '900px', margin: '20px auto', padding: '20px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #eee', paddingBottom: '10px' },
    card: { backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '15px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', borderLeft: '5px solid #0d6efd' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '0.9rem', color: '#666' },
    cardTitle: { margin: '0 0 10px 0', color: '#333', fontSize: '1.2rem' },
    cardBody: { fontSize: '1rem', lineHeight: '1.5', color: '#444', whiteSpace: 'pre-wrap' },
    actions: { display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'flex-end' },
    input: { width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ccc', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '10px', minHeight: '100px', borderRadius: '5px', border: '1px solid #ccc', boxSizing: 'border-box', resize: 'vertical' },
    btn: { padding: '8px 15px', borderRadius: '5px', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: 'white' },
    btnPrimary: { backgroundColor: '#0d6efd' },
    btnDanger: { backgroundColor: '#dc3545' },
    modal: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '500px' }
};

const AnnouncementModal = ({ isOpen, onClose, onSave, initialData }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');

    useEffect(() => {
        if (initialData) {
            setTitle(initialData.title);
            setContent(initialData.content);
        } else {
            setTitle('');
            setContent('');
        }
    }, [initialData, isOpen]);

    const handleSubmit = () => {
        if (!title.trim() || !content.trim()) {
            toast.error("Titolo e contenuto sono obbligatori.");
            return;
        }
        onSave({ title, content });
    };

    if (!isOpen) return null;

    return (
        <div style={styles.modal} onMouseDown={onClose}>
            <div style={styles.modalContent} onMouseDown={e => e.stopPropagation()}>
                <h3>{initialData ? 'Modifica Annuncio' : 'Nuovo Annuncio'}</h3>
                <input 
                    style={styles.input} 
                    placeholder="Titolo (es. Chiusura Estiva)" 
                    value={title} 
                    onChange={e => setTitle(e.target.value)} 
                />
                <textarea 
                    style={styles.textarea} 
                    placeholder="Scrivi il messaggio..." 
                    value={content} 
                    onChange={e => setContent(e.target.value)} 
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                    <button style={{ ...styles.btn, backgroundColor: '#6c757d' }} onClick={onClose}>Annulla</button>
                    <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleSubmit}>Salva</button>
                </div>
            </div>
        </div>
    );
};

export default function Announcements() {
    const { userProfile } = useUser();
    const [announcements, setAnnouncements] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const canEdit = userProfile?.permissions?.announcements === 'completo' || 
                    userProfile?.permissions?.announcements === 'semi-completo' || 
                    userProfile?.role === 'Super Admin' || userProfile?.role === 'Proprietario';

    useEffect(() => {
        if (!userProfile?.centerId) return;
        
        const q = query(
            collection(db, 'centers', userProfile.centerId, 'announcements'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setAnnouncements(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [userProfile]);

    const handleSave = async (data) => {
        try {
            const docData = {
                ...data,
                authorName: userProfile.name,
                authorId: userProfile.uid,
                updatedAt: serverTimestamp()
            };

            if (editingItem) {
                await updateDoc(doc(db, 'centers', userProfile.centerId, 'announcements', editingItem.id), docData);
                toast.success("Annuncio modificato!");
            } else {
                await addDoc(collection(db, 'centers', userProfile.centerId, 'announcements'), {
                    ...docData,
                    createdAt: serverTimestamp()
                });
                toast.success("Annuncio pubblicato!");
            }
            setIsModalOpen(false);
            setEditingItem(null);
        } catch (error) {
            console.error("Errore salvataggio:", error);
            toast.error("Errore durante il salvataggio.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Sei sicuro di voler eliminare questo annuncio?")) {
            try {
                await deleteDoc(doc(db, 'centers', userProfile.centerId, 'announcements', id));
                toast.success("Annuncio eliminato.");
            } catch (error) {
                toast.error("Errore eliminazione.");
            }
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaBullhorn style={{ color: '#0d6efd' }} /> Bacheca Aziendale
                </h2>
                {canEdit && (
                    <button style={{ ...styles.btn, ...styles.btnPrimary, display: 'flex', alignItems: 'center', gap: '5px' }} onClick={() => { setEditingItem(null); setIsModalOpen(true); }}>
                        <FaPlus /> Nuovo Avviso
                    </button>
                )}
            </div>

            {announcements.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#888', marginTop: '40px' }}>Nessun annuncio in bacheca.</p>
            ) : (
                announcements.map(ann => (
                    <div key={ann.id} style={styles.card}>
                        <div style={styles.cardHeader}>
                            <span><strong>{ann.authorName}</strong></span>
                            <span>{ann.createdAt ? moment(ann.createdAt.toDate()).format('DD/MM/YYYY HH:mm') : 'Adesso'}</span>
                        </div>
                        <h3 style={styles.cardTitle}>{ann.title}</h3>
                        <div style={styles.cardBody}>{ann.content}</div>
                        
                        {canEdit && (
                            <div style={styles.actions}>
                                <button style={{ ...styles.btn, backgroundColor: '#ffc107', color: '#333' }} onClick={() => { setEditingItem(ann); setIsModalOpen(true); }}><FaEdit /></button>
                                <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => handleDelete(ann.id)}><FaTrash /></button>
                            </div>
                        )}
                    </div>
                ))
            )}

            <AnnouncementModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSave={handleSave} 
                initialData={editingItem} 
            />
        </div>
    );
}